import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { IonCard, IonCardContent, IonCol, IonGrid, IonIcon, IonRow, IonText } from '@ionic/angular/standalone';
import { TranslateModule } from '@ngx-translate/core';
import {
  EStopType,
  IFreightBasicForCarrier,
  IFreightDriver,
  IFreightVehicle,
  isFreightInternational,
} from '@okcargo-apps/shared-domain';
import { AppTab, ECapabilities } from '@okcargo-apps/shared-domain-ionic';
import { AccountService } from '../../account';
import { FreightDocumentationLinksComponent } from '../../freight-documentation/freight-documentation-links/freight-documentation-links.component';
import { FreightCarrierAssignationButtonComponent } from '../freight-carrier-assignation-button/freight-carrier-assignation-button.component';
import { FreightCarrierInprogressCardBasicComponent } from '../freight-carrier-inprogress-card-basic/freight-carrier-inprogress-card-basic.component';
import { FreightCarrierStopButtonComponent } from '../freight-carrier-stop-button/freight-carrier-stop-button.component';

@Component({
  selector: 'lib-freight-carrier-inprogress-card',
  templateUrl: './freight-carrier-inprogress-card.component.html',
  styleUrls: ['./freight-carrier-inprogress-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    CommonModule,
    IonCol,
    IonRow,
    IonGrid,
    IonIcon,
    RouterLink,
    RouterLinkActive,
    IonCardContent,
    IonCard,
    TranslateModule,
    FreightCarrierStopButtonComponent,
    FreightCarrierAssignationButtonComponent,
    FreightCarrierInprogressCardBasicComponent,
    FreightDocumentationLinksComponent,
    IonText,
  ],
})
export class FreightCarrierInprogressCardComponent {
  public readonly hideColumns = input<boolean>(false);

  public readonly freight = input.required<IFreightBasicForCarrier>();

  private readonly accountService = inject(AccountService);

  readonly EStopType = EStopType;

  // TODO implement this feature
  minDateForStart: Date = new Date(Date.now() + 2 * 24 * 60 * 60 * 1000); // 2 days

  /** Vehicles can be assigned with CRUD_VEHICLES or by drivers (INTERACT_WITH_STOPS); CRUD_VEHICLES prevails. */
  protected readonly canManageVehicles = computed(
    () =>
      this.accountService.hasCapability(ECapabilities.CRUD_VEHICLES) ||
      this.accountService.hasCapability(ECapabilities.INTERACT_WITH_STOPS),
  );
  protected readonly canViewDrivers = computed(() => this.accountService.hasCapability(ECapabilities.VIEW_DRIVERS));
  protected readonly canInteractWithStops = computed(() =>
    this.accountService.hasCapability(ECapabilities.INTERACT_WITH_STOPS),
  );

  /**
   * Missing vehicles require attention (like the DeCA cards): the driver and plates are needed to
   * generate a DeCA, unless the freight is international.
   */
  protected readonly isMissingVehicles = computed(
    () => this.freight().vehicles.length === 0 && !isFreightInternational(this.freight().stopsBasic),
  );

  protected AppTab = AppTab;

  public printDrivers(drivers: IFreightDriver[]): string {
    const driversArrayTxt: string[] = [];
    drivers.forEach((driver) => {
      if (driver.name !== undefined && driver.name !== '') {
        driversArrayTxt.push(driver.name);
      }
    });
    return driversArrayTxt.join(', ');
  }

  public printVehicles(vehicles: IFreightVehicle[]): string {
    const vehiclesArrayTxt: string[] = [];
    vehicles.forEach((vehicle) => {
      if (vehicle.plate !== undefined && vehicle.plate !== '') {
        vehiclesArrayTxt.push(vehicle.plate);
      }
    });
    return vehiclesArrayTxt.join(', ');
  }
}
